import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Notifications } from "./Notifications";
import { TeamsButton, TeamsLogo, TeamsNotice } from "./TeamsConnect";
import { PHONE, useMediaQuery } from "../lib/screen";
import { useTheme } from "../lib/theme";
import { endpoints, signOut, type Me } from "../lib/api";
// The Polaris logo (brand files): the wordmark in the open sidebar, the ring
// symbol when it's collapsed, white on dark and black on light.
import wordmarkWhite from "../assets/polaris-wordmark-white.png";
import wordmarkBlack from "../assets/polaris-wordmark-black.png";
import iconWhite from "../assets/polaris-icon-white.png";
import iconDark from "../assets/polaris-icon-dark.png";

const PAGES = [
  { to: "/", label: "Dashboard", icon: <IconGrid />, end: true },
  { to: "/meetings", label: "Meetings", icon: <IconList />, end: false },
  { to: "/projects", label: "Projects", icon: <IconFolder />, end: false },
  { to: "/calendar", label: "Calendar", icon: <IconCalendar />, end: false },
  { to: "/record", label: "Record", icon: <IconMic />, end: false },
  { to: "/upload", label: "Upload", icon: <IconUpload />, end: false },
  { to: "/personalization", label: "Personalization", icon: <IconPen />, end: false },
];

/** The app frame: a sidebar with the logo and the pages, a top bar with
 * notifications, the theme and your account, and the page filling the rest.
 * On a phone the sidebar is a menu that slides in over the page, opened from
 * the top bar. */
export function Shell({ me }: { me: Me | null }) {
  const phone = useMediaQuery(PHONE);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [storedCollapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar") === "collapsed";
    } catch {
      return false;
    }
  });
  const [theme, toggleTheme] = useTheme();
  // The phone menu is always the full sidebar: there's room, and icons alone
  // would leave you guessing.
  const collapsed = storedCollapsed && !phone;

  // Picking a page closes the menu, and so does leaving phone width.
  useEffect(() => setMenuOpen(false), [location.pathname, phone]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  function toggleSidebar() {
    setCollapsed((was) => {
      const now = !was;
      try {
        localStorage.setItem("sidebar", now ? "collapsed" : "open");
      } catch {
        /* remembering it is a nicety */
      }
      return now;
    });
  }

  return (
    <div className="shell" data-collapsed={collapsed} data-menu={menuOpen ? "open" : undefined}>
      <aside className="sidebar" id="app-menu">
        <div className={`sidebar-top${collapsed ? " collapsed" : ""}`}>
          {collapsed ? (
            <img className="polaris-icon" src={theme === "dark" ? iconWhite : iconDark} alt="Polaris Technologies" />
          ) : (
            <>
              <img
                className="polaris-wordmark"
                src={theme === "dark" ? wordmarkWhite : wordmarkBlack}
                alt="Polaris Technologies"
              />
              <span className="brand">Meeting Agent</span>
            </>
          )}
        </div>

        <nav>
          {PAGES.map((page) => (
            <NavLink
              key={page.to}
              to={page.to}
              end={page.end}
              className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}
              title={collapsed ? page.label : undefined}
            >
              <span className="nav-icon" aria-hidden>{page.icon}</span>
              {!collapsed && <span className="label">{page.label}</span>}
            </NavLink>
          ))}
        </nav>

        <button
          className="nav-link sidebar-collapse"
          onClick={toggleSidebar}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <span className="nav-icon" aria-hidden>{collapsed ? "»" : "«"}</span>
          {!collapsed && <span className="label">Collapse</span>}
        </button>
      </aside>

      {phone && menuOpen && <div className="scrim" onClick={() => setMenuOpen(false)} aria-hidden />}

      <div className="frame">
        <header className="topbar">
          {phone && (
            <>
              <button
                className="topbar-button menu-button"
                onClick={() => setMenuOpen((was) => !was)}
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                aria-controls="app-menu"
                aria-expanded={menuOpen}
              >
                <IconMenu />
              </button>
              <img className="topbar-logo" src={theme === "dark" ? iconWhite : iconDark} alt="Polaris Technologies" />
            </>
          )}
          <span className="topbar-space" />
          {me?.is_admin && <AdminView me={me} />}
          <TeamsButton me={me} />
          <Notifications />
          <button
            className="topbar-button"
            onClick={toggleTheme}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? <IconSun /> : <IconMoon />}
          </button>
          {me && <Profile me={me} />}
        </header>
        <main className="main">
          <TeamsNotice />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Your initials; opens your email and Log out. */
function Profile({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const queryClient = useQueryClient();
  const box = useRef<HTMLDivElement>(null);
  useClickOutside(box, open, () => setOpen(false));

  return (
    <div className="topbar-menu" ref={box}>
      <button
        className="avatar"
        onClick={() => setOpen((was) => !was)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={me.email}
      >
        {initials(me.name || me.email)}
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-head">
            <strong>{me.name || me.email.split("@")[0]}</strong>
            <span>{me.email}{me.is_admin ? " · admin" : ""}</span>
          </div>
          {me.teams?.connected && (
            <div className="menu-teams">
              <TeamsLogo size={16} />
              <span>
                <strong>Teams calendar</strong>
                <small>{me.teams.email}{me.teams.error ? " · last sync failed" : ""}</small>
              </span>
              <button
                className="btn-text"
                disabled={disconnecting}
                onClick={async () => {
                  if (!window.confirm("Disconnect your Teams calendar? Its upcoming meetings leave your list "
                    + "and won't get the bot. Recordings stay.")) return;
                  setDisconnecting(true);
                  try {
                    await endpoints.disconnectTeams(me.csrf_token);
                    await queryClient.invalidateQueries({ queryKey: ["me"] });
                    queryClient.invalidateQueries({ queryKey: ["recordings"] });
                  } finally {
                    setDisconnecting(false);
                  }
                }}
              >
                {disconnecting ? "Disconnecting…" : "Disconnect"}
              </button>
            </div>
          )}
          <EmailMinutesSwitch me={me} />
          <button
            role="menuitem"
            className="menu-item"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              signOut(me.csrf_token);
            }}
          >
            {signingOut ? "Logging out…" : "Log out"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Admins only: the lists show everyone's meetings and projects, or only your
 * own, as anyone else sees them. Opening, stopping and renaming any meeting
 * stay an admin's either way. */
function AdminView({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const everyone = me.sees_all ?? true;
  const change = useMutation({
    mutationFn: (next: boolean) => endpoints.setAdminView(next, me.csrf_token),
    onSuccess: ({ sees_all }) => {
      queryClient.setQueryData<Me>(["me"], { ...me, sees_all });
      // Every list, refetched with the new scope.
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== "me" });
    },
  });
  const shown = change.isPending ? Boolean(change.variables) : everyone;
  return (
    <div className="segmented admin-view" role="radiogroup" aria-label="Meetings shown"
         title="Admin: whose meetings and projects the lists show">
      {([[false, "Mine"], [true, "Everyone"]] as const).map(([value, label]) => (
        <button key={label} role="radio" aria-checked={shown === value} className={shown === value ? "on" : ""}
                disabled={change.isPending} onClick={() => shown !== value && change.mutate(value)}>
          {label}
        </button>
      ))}
    </div>
  );
}

/** "Email me meeting minutes": on by default; the minutes of meetings you were
 * invited to arrive by email five minutes after they're written. */
function EmailMinutesSwitch({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const on = me.email_minutes ?? true;
  const change = useMutation({
    mutationFn: (enabled: boolean) => endpoints.setEmailMinutes(enabled, me.csrf_token),
    onSuccess: ({ email_minutes }) => queryClient.setQueryData<Me>(["me"], { ...me, email_minutes }),
  });
  return (
    <label className="menu-item menu-switch">
      <span>
        Email me meeting minutes
        <small>After each meeting you were invited to</small>
      </span>
      <input type="checkbox" role="switch" checked={change.isPending ? Boolean(change.variables) : on}
             disabled={change.isPending} onChange={(event) => change.mutate(event.target.checked)} />
    </label>
  );
}

/** Closes a menu on a click anywhere else, or on Esc. */
export function useClickOutside(box: React.RefObject<HTMLElement | null>, active: boolean, close: () => void) {
  useEffect(() => {
    if (!active) return;
    const onClick = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && close();
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [box, active, close]);
}

function initials(name: string): string {
  const words = name.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return ((words[0]?.[0] ?? "?") + (words.length > 1 ? words[words.length - 1][0] : "")).toUpperCase();
}

// --- line icons ----------------------------------------------------------------------

function Svg({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}
function IconGrid() {
  return <Svg><rect x="4" y="4" width="7" height="7" rx="1" /><rect x="13" y="4" width="7" height="7" rx="1" /><rect x="4" y="13" width="7" height="7" rx="1" /><rect x="13" y="13" width="7" height="7" rx="1" /></Svg>;
}
function IconList() {
  return <Svg><path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" /></Svg>;
}
function IconFolder() {
  return <Svg><path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></Svg>;
}
function IconCalendar() {
  return <Svg><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M4 10h16M9 3v4M15 3v4" /></Svg>;
}
function IconMic() {
  return <Svg><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0014 0M12 18v3" /></Svg>;
}
function IconUpload() {
  return <Svg><path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" /></Svg>;
}
function IconPen() {
  return <Svg><path d="M5 19l1-4L16 5l3 3L9 18z" /></Svg>;
}
function IconMenu() {
  return <Svg><path d="M4 7h16M4 12h16M4 17h16" /></Svg>;
}
function IconSun() {
  return <Svg><circle cx="12" cy="12" r="4" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" /></Svg>;
}
function IconMoon() {
  return <Svg><path d="M20 14.5A8 8 0 019.5 4 7 7 0 1020 14.5z" /></Svg>;
}
export function IconBell() {
  return <Svg><path d="M6 16V11a6 6 0 0112 0v5l1.5 2h-15zM10 20a2 2 0 004 0" /></Svg>;
}
